import {
  GridApi,
  IRowNode,
  RowClassRules,
  SelectionChangedEvent,
} from 'ag-grid-community'

// 選択行の枠線（agGrid.scss の .ag-row-selected::after）を「連続選択はひとつの枠」に
// 見せるための補助。各選択行がブロック内のどの位置か（先頭/末尾/単独）を表示順で判定し
// bulksheet-row-selected-* クラスを付与する。DOM順に依存しない（行アニメーションを維持できる）。
// BulkSheet / BulkSheetView の両コンテナから共通利用する。
const SELECTION_BLOCK_POSITION_CLASSES = [
  'bulksheet-row-selected-single',
  'bulksheet-row-selected-top',
  'bulksheet-row-selected-bottom',
]

const isRowSelectedAtIndex = (api: GridApi, index: number): boolean =>
  !!api.getDisplayedRowAtIndex(index)?.isSelected()

type SelectionBlockPosition = 'single' | 'top' | 'bottom' | 'middle' | null
const selectionBlockPosition = (
  api: GridApi,
  node: IRowNode
): SelectionBlockPosition => {
  if (!node.isSelected()) return null
  const index = node.rowIndex
  if (index === null) return null
  const above = isRowSelectedAtIndex(api, index - 1)
  const below = isRowSelectedAtIndex(api, index + 1)
  if (!above && !below) return 'single'
  if (!above) return 'top'
  if (!below) return 'bottom'
  return 'middle'
}

// 行レンダリング時（スクロール/ソート/データ更新）に評価され、画面外から戻った行にも
// 正しい位置クラスを付ける。選択変更時の即時反映は createSelectionRowFrameHandler が担う。
export const SELECTION_BLOCK_ROW_CLASS_RULES: RowClassRules = {
  'bulksheet-row-selected-single': params =>
    selectionBlockPosition(params.api, params.node) === 'single',
  'bulksheet-row-selected-top': params =>
    selectionBlockPosition(params.api, params.node) === 'top',
  'bulksheet-row-selected-bottom': params =>
    selectionBlockPosition(params.api, params.node) === 'bottom',
}

// 表示中の行DOMの位置クラスを選択状態に合わせて直接付け替える。redrawRows と違いDOMを
// 作り直さないため、連続選択でもカクつかず、フォーカス/セル範囲にも触れない。
// 画面外の行は再レンダリング時に rowClassRules が付けるため、表示中の行のみ更新する。
const refreshSelectionBlockClasses = (api: GridApi, root: HTMLElement) => {
  api.getRenderedNodes().forEach(node => {
    if (node.id === undefined) return
    const position = selectionBlockPosition(api, node)
    // 同一行はピン留め左/中央など複数コンテナにDOMが存在するため全て更新する。
    const rowElements = root.querySelectorAll<HTMLElement>(
      `.ag-row[row-id="${node.id}"]`
    )
    rowElements.forEach(element => {
      element.classList.remove(...SELECTION_BLOCK_POSITION_CLASSES)
      if (
        position === 'single' ||
        position === 'top' ||
        position === 'bottom'
      ) {
        element.classList.add(`bulksheet-row-selected-${position}`)
      }
    })
  })
}

// onSelectionChanged 用ハンドラを生成する。連続選択(shift+↓等)によるクラス更新を
// requestAnimationFrame で1フレームに集約し、DOMを作り直さずに反映する
// （カクつき/フォーカス・セル範囲への干渉を回避）。
// getRoot はグリッドのテーマルート要素（ag-theme-balham）を返すこと。選択枠の DOM
// クエリをそのグリッド配下に限定するために使う。
export const createSelectionRowFrameHandler = (
  getRoot: () => HTMLElement | null
): ((event: SelectionChangedEvent) => void) => {
  let rafId: number | null = null
  return event => {
    if (rafId !== null) {
      cancelAnimationFrame(rafId)
    }
    rafId = requestAnimationFrame(() => {
      rafId = null
      const { api } = event
      const root = getRoot()
      if (api.isDestroyed() || !root) return
      refreshSelectionBlockClasses(api, root)
    })
  }
}
